import copy from "copy-to-clipboard";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  useFilters,
  useGlobalFilter,
  useRowSelect,
  useTable,
} from "react-table";
import styled from "styled-components";
import "../App.css";
import DualAxesChart from "./DualAxesChart";
import DualAxesChartComparison from "./DualAxesChartComparison";

const apiEndpoint = process.env.REACT_APP_API_ENDPOINT

const URL = {
  GENERATE_SCREENSHOT: `${apiEndpoint}/screenshot`,
  POLLER: `${apiEndpoint}/poller`,
  GENERATE: `${apiEndpoint}/generate`,
};

// const URL = {
//   GENERATE_SCREENSHOT: 'http://127.0.0.1:8000/screenshot',
//   POLLER: 'http://127.0.0.1:8000/poller',
//   GENERATE: 'http://127.0.0.1:8000/generate',
// }

const Styles = styled.div`
  .control-button button {
    margin-left: 20px;
  }
`;
const TableStyles = styled.div`
  padding: 1rem;
  display: flex;
  justify-content: center; /* This will center the table container */

  .image-container {
    position: relative;
    display: inline-block;
  }

  .image-container .normal {
    max-width: 200px;
    height: auto;
  }

  .image-container:hover .magnified {
    visibility: visible;
  }

  .magnified {
    visibility: hidden;
    position: fixed; /* Change from absolute to fixed */
    top: 10px; /* Set position from the top */
    left: 10px; /* Set position from the left */
    max-width: 1000px;
    height: auto;
    border: 1px solid #ccc;
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1000;
  }

  /* Style for table headers */
  .table-header {
    background-color: #64b5f6; /* More distinct blue */
    color: #002984; /* Dark blue for contrast */
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 2px solid #003c8f; /* Darker blue */
    padding: 10px;
  }

  /* Optionally, style for table rows for better readability */
  .table-row {
    border-bottom: 1px solid #dee2e6; /* Bootstrap light border color */
    padding: 10px;
    cursor: pointer; /* Add this line */
  }

  .table-row:hover {
    background-color: #e0f7fa; /* Change background color on hover for better UX */
  }

  .table-row.selected {
    background-color: #e0f7fa; /* Change background color when selected */
  }

  table {
    margin: 0 auto; /* This will center the table */
    border-spacing: 0;
    border: 1px solid black;

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid black;
      border-right: 1px solid black;

      :last-child {
        border-right: 0;
      }
    }
  }
`;

// Define a default UI for filtering
function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  return <div></div>;
}

function IndeterminateCheckbox({ indeterminate, ...rest }) {
  const defaultRef = React.useRef();
  const ref = rest.indeterminate ? defaultRef : null;

  React.useEffect(() => {
    if (defaultRef.current) {
      defaultRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <>
      <input type="checkbox" ref={ref} {...rest} />
    </>
  );
}

// This is a custom UI for our 'between' or number range
// filter. It uses two number boxes and filters rows to
// ones that have values between the two
function NumberRangeColumnFilter({
  column: { filterValue = [], preFilteredRows, setFilter, id },
}) {
  const [min, max] = React.useMemo(() => {
    console.log(preFilteredRows);
    // Initialize min and max based on the first row's value if available
    if (preFilteredRows.length) {
      let min = preFilteredRows[0].values[id];
      let max = preFilteredRows[0].values[id];

      preFilteredRows.forEach((row) => {
        min = Math.min(row.values[id], min);
        max = Math.max(row.values[id], max);
      });
      console.log(min, max);
      return [min, max];
    }
    // Default values if preFilteredRows is empty
    return [0, 0];
  }, [id, preFilteredRows]);

  return (
    <div
      style={{
        display: "flex",
      }}
    >
      <input
        value={filterValue[0] || ""}
        type="number"
        onChange={(e) => {
          const val = e.target.value;
          setFilter((old = []) => [
            val ? parseInt(val, 10) : undefined,
            old[1],
          ]);
        }}
        placeholder={`min`}
        style={{
          width: "70px",
          marginRight: "0.5rem",
        }}
      />
      to
      <input
        value={filterValue[1] || ""}
        type="number"
        onChange={(e) => {
          const val = e.target.value;
          setFilter((old = []) => [
            old[0],
            val ? parseInt(val, 10) : undefined,
          ]);
        }}
        placeholder={`max`}
        style={{
          width: "70px",
          marginLeft: "0.5rem",
        }}
      />
    </div>
  );
}

const spinner =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><circle cx="50" cy="50" r="20" fill="none" stroke="%23000" stroke-width="3" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="1s" repeatCount="indefinite" /></circle></svg>';
const errorImage =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100%" height="100%" fill="%23FFCCCC"/><text x="50%" y="50%" alignment-baseline="middle" text-anchor="middle" fill="%23FF0000" font-size="24">Error</text></svg>';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Our table component
function Table({ columns, data, setData }) {
  const [showModal, setShowModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [canRetry, setCanRetry] = useState(false);
  const [comparisonScreenshots, setComparisonScreenshots] = useState([]);

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      // fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    state: { selectedRowIds },
  } = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
    },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!
    useRowSelect
  );

  const getComparisonScreenshots = async () => {
    setShowModal(true); // Show the modal
    setIsGenerating(true);
    setCanRetry(false);
    setComparisonScreenshots([]);
    let selectedUrls = selectedFlatRows.map((row) => row.original.url);
    if (selectedFlatRows.length > 11) {
      window.alert(
        "You have selected more than 11 URLs. Only the first 11 URLs will be compared."
      );
      selectedUrls = selectedUrls.slice(0, 11);
    }
    getComparisonScreenshotsRequest(
      selectedUrls,
      (response) => {
        setIsGenerating(false);
        setComparisonScreenshots(
          showComparisonScreenshot(selectedUrls, {
            image: response,
          })
        );
      },
      (err) => {
        setIsGenerating(false);
        setCanRetry(true);
        window.alert("Error generating comparison screenshots. Retry again.");
      }
    );
  };

  const getScreenshots = () => {
    // const selectedUrls = selectedFlatRows.map((row) => row.original.url);
    selectedFlatRows.map((row) => {
      // set spinner
      setData((prevData) =>
        replaceResultWithURL(prevData, row.original.url, {
          image: spinner,
        })
      );
      getScreenshotRequest(row.original.url, setData);
    });
  };

  const handleDelete = () => {
    setData((prevData) =>
      prevData.filter((_, index) => !selectedRowIds[index])
    );
  };

  const getComparisonScreenshotsRequest = async (
    urls,
    successHandler,
    errorHandler
  ) => {
    const jobName = "compare";
    try {
      const res = await fetch(URL.GENERATE_SCREENSHOT, {
        method: "POST",
        body: JSON.stringify({
          url: urls,
          job_name: jobName,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      await res.json(); // dont do anything and wait for the poller to do the job
      // 添加5秒延时
      poller(urls, jobName, successHandler, errorHandler);
    } catch (err) {
      errorHandler(err);
    }
  };

  const getScreenshotRequest = async (url, setData) => {
    const jobName = "perf";
    const successHandler = (response) => {
      setData((prevData) =>
        replaceResultWithURL(prevData, url, {
          image: response,
        })
      );
    };
    const errorHandler = () => {
      setData((prevData) =>
        replaceResultWithURL(prevData, url, {
          image: errorImage,
        })
      );
    };
    try {
      const res = await fetch(URL.GENERATE_SCREENSHOT, {
        method: "POST",
        body: JSON.stringify({
          url: url,
          job_name: jobName,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      await res.json(); // dont do anything and wait for the poller to do the job
      // 添加5秒延时
      poller(url, jobName, successHandler, errorHandler);
    } catch (err) {
      console.error(err);
      setData((prevData) =>
        replaceResultWithURL(prevData, url, {
          image: errorImage,
        })
      );
    }
  };

  const poller = async (url, jobName, successHandler, errorHandler) => {
    try {
      const data = {
        job_name: jobName,
        url: url,
      };
      // Convert data object to query string
      const queryString = new URLSearchParams(data).toString();
      const res = await fetch(`${URL.POLLER}?${queryString}`);
      const response = await res.json();
      if (response.status === "ok") {
        console.log("success");
        console.log(response);
        successHandler(response.image);
      } else if (response.status === "missing") {
        // 添加5秒延时
        await new Promise((resolve) => setTimeout(resolve, 5000));
        // await poller(url, jobName, setData);
        await poller(url, jobName, successHandler, errorHandler);
      } else if (response.status === "running") {
        // 添加5秒延时
        await new Promise((resolve) => setTimeout(resolve, 5000));
        await poller(url, jobName, successHandler, errorHandler);
        // await poller(url, jobName, setData);
      }
    } catch (err) {
      console.error(err);
      errorHandler();
    }
  };

  const replaceResultWithURL = (result, url, newData) => {
    // Map through the data and replace the image URL for the matching row
    return result.map((row) => {
      if (row.url === url) {
        // return { ...row, ...newData };
        return { ...row, chart: <DualAxesChart allData={[url, newData]} /> };
      }
      return row;
    });
  };

  const showComparisonScreenshot = (selectedUrls, newData) => {
    return <DualAxesChartComparison allData={[selectedUrls, newData]} />;
  };

  const copyToClipboard = function () {
    let copy_text = "";
    for (let i = 0; i < selectedFlatRows.length; i++) {
      const row = rows[i].original; // Access the original row data
      copy_text += row.name + " " + row.url + "\n";
      copy_text += row["description-translated"] + "\n\n";
    }
    copy(copy_text);
    alert("Copied to clipboard");
  };

  // We don't want to render all of the rows for this example, so cap
  // it for this use case
  // const firstPageRows = rows.slice(0, 10)
  const firstPageRows = rows;
  return (
    <>
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Comparison Screenshots</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Generating comparison screenshot for the (
            {selectedFlatRows.length <= 11 ? selectedFlatRows.length : 11}/
            {selectedFlatRows.length}) URLs:
          </p>
          {isGenerating && <Spinner animation="border" role="status"></Spinner>}
          {canRetry && (
            <Button variant="primary" onClick={getComparisonScreenshots}>
              Retry
            </Button>
          )}
          <div>
            {/* 直接渲染 comparisonScreenshots */}
            {comparisonScreenshots}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Row className="mt-3 mb-3 control-button">
        <Col className="d-flex justify-content-end">
          <Button
            className="btn btn-success"
            onClick={getComparisonScreenshots}
            // disabled={selectedFlatRows.length === 0}
          >
            Get Comparison Screenshot ({selectedFlatRows.length})
          </Button>
          <Button
            className="btn btn-success"
            onClick={getScreenshots}
            disabled={selectedFlatRows.length === 0}
          >
            Get Screenshot ({selectedFlatRows.length})
          </Button>
          <Button
            className="btn btn-warning"
            onClick={(e) => copyToClipboard()}
            disabled={selectedFlatRows.length === 0}
          >
            Copy ({selectedFlatRows.length})
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={selectedFlatRows.length === 0}
            className="mr-2" // Add some margin to separate it from the other buttons
          >
            Delete ({selectedFlatRows.length})
          </Button>
        </Col>
      </Row>
      <Row>
        <TableStyles>
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr
                  {...headerGroup.getHeaderGroupProps()}
                  className="table-header"
                >
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>
                      {column.render("Header")}
                      {/* Render the columns filter UI */}
                      <div>
                        {column.canFilter ? column.render("Filter") : null}
                      </div>
                    </th>
                  ))}
                  s
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {firstPageRows.map((row, i) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className={`table-row ${row.isSelected ? "selected" : ""}`}
                    onClick={() => row.toggleRowSelected()}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableStyles>
        <br />
        {/* <div>Showing the first 20 results of {rows.length} rows</div>
      <div>
        <pre>
          <code>{JSON.stringify(state.filters, null, 2)}</code>
        </pre>
      </div> */}
      </Row>
    </>
  );
}

function Analyze() {
  const [requirements, setRequirements] = useState("");
  const [result, setResult] = useState([]);
  const [regenerateDisabled, setRegenerateDisabled] = useState(false);
  const [generateMoreDisabled, setGenerateMoreDisabled] = useState(false);
  // State variable to track collapse state
  const [open, setOpen] = useState(false);
  // Define a new state variable to hold the form data
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    title: "",
    description: "",
    titleTranslated: "",
    descriptionTranslated: "",
    // pages: "",
    traffic: "",
    image: "",
  });

  useEffect(() => {}, []);

  const regenerate = function () {
    setResult([]);
    setRegenerateDisabled(true);
    fetch(URL.GENERATE, {
      method: "POST",
      body: JSON.stringify({
        input: requirements,
      }),
      headers: {
        "Content-type": "application/json; charset=UTF-8",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        // Handle data
        setResult(data);
        setRegenerateDisabled(false);
      })
      .catch((err) => {
        console.log(err.message);
        alert(err.message);
        setRegenerateDisabled(false);
      });
  };

  const generateMore = function () {
    setGenerateMoreDisabled(true);
    fetch(URL.GENERATE, {
      method: "POST",
      body: JSON.stringify({
        input: requirements,
      }),
      headers: {
        "Content-type": "application/json; charset=UTF-8",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        // Handle data
        setResult(result.concat(data));
        setGenerateMoreDisabled(false);
      })
      .catch((err) => {
        console.log(err.message);
        alert(err.message);
        setGenerateMoreDisabled(false);
      });
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();

    // Basic URL validation
    const urlPattern =
      /^(https?:\/\/)?(([\da-z.-]+).([a-z.]{2,6})|((\d{1,3}.){3}\d{1,3}))([\/\w .-]*)*\/?$/;
    if (!urlPattern.test(formData.url)) {
      alert("Please enter a valid URL.");
      return;
    }

    setResult((prevData) => [...prevData, formData]);
    setFormData({
      name: "",
      url: "",
      title: "",
      description: "",
      titleTranslated: "",
      descriptionTranslated: "",
      // pages: "",
      traffic: "",
      image: "",
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Result",
        columns: [
          {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            Cell: ({ row }) => (
              <div>
                <input type="checkbox" {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          {
            Header: "Name",
            accessor: "name",
          },
          {
            Header: "URL",
            accessor: "url",
            Cell: ({ value }) => (
              <a href={value} target="_blank" rel="noopener noreferrer">
                {value}
              </a>
            ),
          },
          {
            Header: "Title",
            accessor: "title",
          },
          {
            Header: "Description",
            accessor: "description",
          },
          {
            Header: "Title-translated",
            accessor: "title-translated",
          },
          {
            Header: "Description-translated",
            accessor: "description-translated",
          },
          // {
          //   Header: "Pages",
          //   accessor: "pages",
          //   Filter: NumberRangeColumnFilter,
          //   filter: "between",
          // },
          {
            Header: "Traffic",
            accessor: "traffic",
            Filter: NumberRangeColumnFilter,
            filter: "between",
          },
          {
            Header: "Chart",
            accessor: "chart",
            Cell: ({ value }) => <div>{value}</div>,
          },
        ],
      },
    ],
    []
  );

  // const data = React.useMemo(() => makeData(100000), [])

  return (
    <Styles>
      <Container>
        <Row className="mt-3">
          <h1>AI Sales Tool</h1>
        </Row>
        <Row className="mt-3 mb-3">
          <Form.Group as={Col}>
            <Form.Label htmlFor="inputRequirement">Requirements:</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              id="inputRequirement"
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              placeholder="Enter requirements"
              style={{ fontSize: "1.5rem", height: "auto", padding: "1rem" }} // Adjust font size, height, and padding
            />
          </Form.Group>
        </Row>
        <Row>
          <Col>
            <Button
              className="btn btn-success"
              style={{ marginRight: "20px" }}
              onClick={(e) => regenerate()}
              disabled={regenerateDisabled}
            >
              Regenerate
            </Button>
            <Button
              className="btn btn-success"
              onClick={(e) => generateMore()}
              disabled={generateMoreDisabled}
            >
              Generate more
            </Button>
          </Col>
          <Col className="d-flex justify-content-end">
            {(regenerateDisabled || generateMoreDisabled) && (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            )}
          </Col>
        </Row>

        <Row>
          <Table columns={columns} data={result} setData={setResult} />
          <Row className="mb-3 mt-5">
            <Col>
              {/* Button to toggle collapse state */}
              <Button
                onClick={() => setOpen(!open)}
                aria-controls="collapse-form"
                aria-expanded={open}
                variant="outline-primary"
                className="mb-2"
              >
                Add New Entry
              </Button>
              <Collapse in={open}>
                <div>
                  <h2>Add New Entry</h2> {/* Title for the form */}
                  <Form onSubmit={handleFormSubmit}>
                    <Row className="mb-3">
                      {" "}
                      {/* Grouping form fields into a row */}
                      <Form.Group as={Col} controlId="formName">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                      <Form.Group as={Col} controlId="formUrl">
                        <Form.Label>URL</Form.Label>
                        <Form.Control
                          type="url"
                          placeholder="Enter URL"
                          name="url"
                          value={formData.url}
                          onChange={handleInputChange}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid URL.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Row>
                    <Button variant="primary" type="submit">
                      Add Row
                    </Button>
                  </Form>
                </div>
              </Collapse>
            </Col>
          </Row>
        </Row>
      </Container>
    </Styles>
  );
}

export default Analyze;
